import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, ChevronDown, Check } from "lucide-react";
import { format, differenceInCalendarDays, parseISO, isValid } from "date-fns";
import { toast } from "sonner";
import PaymentDialog from "@/components/PaymentDialog";
import PowerTranzDialog from "@/components/PowerTranzDialog";
import BookExperiences from "./book/BookExperiences";
import BookSummary from "./book/BookSummary";
import BankTransferDialog from "@/components/BankTransferDialog";

export default function Book() {
    const { user } = useAuth();
    const { t } = useT();
    const { format: formatMoney } = useCurrency();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [cabins, setCabins] = useState([]);
    const [experiences, setExperiences] = useState([]);
    const [bookedDates, setBookedDates] = useState([]);
    const [loadingAvail, setLoadingAvail] = useState(false);
    const [range, setRange] = useState({ from: undefined, to: undefined });
    const [form, setForm] = useState({
        cabin_id: "",
        guests: 2,
        guest_name: user?.name || "",
        guest_email: user?.email || "",
        guest_phone: "",
        notes: "",
        experiences: {},
    });
    const [submitting, setSubmitting] = useState(false);
    const [loyalty, setLoyalty] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState("reserve");
    const [checkout, setCheckout] = useState(null); // { booking, clientSecret, publishableKey }
    const [ptCheckout, setPtCheckout] = useState(null); // PowerTranz booking
    const [bankInfo, setBankInfo] = useState(null); // { booking, details }

    useEffect(() => {
        api.get("/cabins").then((r) => setCabins(r.data));
        api.get("/experiences").then((r) => setExperiences(r.data));
        api.get("/payments/methods")
            .then((r) => {
                const m = r.data.methods || [];
                setPaymentMethods(m);
                setSelectedMethod(m.length ? m[0].key : "reserve");
            })
            .catch(() => setPaymentMethods([]));
    }, []);

    // Handle PayPal redirect return (?pp=capture&token=...&booking_id=...)
    useEffect(() => {
        const pp = params.get("pp");
        if (pp === "capture") {
            const orderId = params.get("token");
            const bookingId = params.get("booking_id");
            if (orderId && bookingId) {
                api.post("/payments/paypal/capture", { booking_id: bookingId, order_id: orderId })
                    .then(() => { toast.success(t("book.paymentSuccess")); navigate(user ? "/dashboard" : "/"); })
                    .catch((e) => toast.error(e.response?.data?.detail || t("book.paymentFailed")));
            }
        } else if (pp === "cancel") {
            toast.message(t("book.paymentCancelled"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!user) { setLoyalty(null); return; }
        api.get("/profile/loyalty").then((r) => setLoyalty(r.data)).catch(() => setLoyalty(null));
    }, [user]);

    useEffect(() => {
        if (user) setForm((f) => ({ ...f, guest_name: f.guest_name || user.name, guest_email: f.guest_email || user.email }));
    }, [user]);

    useEffect(() => {
        if (!form.cabin_id) { setBookedDates([]); return; }
        setLoadingAvail(true);
        setRange({ from: undefined, to: undefined });
        api.get(`/cabins/${form.cabin_id}/availability`)
            .then((r) => setBookedDates(r.data.booked_dates || []))
            .catch(() => setBookedDates([]))
            .finally(() => setLoadingAvail(false));
    }, [form.cabin_id]);

    const bookedSet = useMemo(() => new Set(bookedDates), [bookedDates]);

    // ---- Deep-link prefill (?cabin=slug|id & in=YYYY-MM-DD & out=YYYY-MM-DD & guests=N) ----
    const prefillRef = useRef(false);
    const pendingDatesRef = useRef(null);
    useEffect(() => {
        if (prefillRef.current || cabins.length === 0) return;
        prefillRef.current = true;
        const cabinParam = params.get("cabin");
        const updates = {};
        if (cabinParam) {
            const match = cabins.find((c) => c.id === cabinParam || c.slug === cabinParam);
            if (match) updates.cabin_id = match.id;
        }
        const g = parseInt(params.get("guests"), 10);
        if (g === 1 || g === 2) updates.guests = g;
        if (Object.keys(updates).length) setForm((f) => ({ ...f, ...updates }));
        const ci = params.get("in") || params.get("check_in");
        const co = params.get("out") || params.get("check_out");
        if (ci && co) pendingDatesRef.current = { ci, co };
    }, [cabins, params]);

    // Apply prefilled dates once the selected cabin's availability has loaded.
    useEffect(() => {
        const pd = pendingDatesRef.current;
        if (!pd || loadingAvail || !form.cabin_id) return;
        pendingDatesRef.current = null;
        const from = parseISO(pd.ci);
        const to = parseISO(pd.co);
        const today0 = new Date(new Date().setHours(0, 0, 0, 0));
        if (!isValid(from) || !isValid(to) || to <= from || from < today0) return;
        let ok = true;
        const d = new Date(from);
        while (d < to) {
            if (bookedSet.has(format(d, "yyyy-MM-dd"))) { ok = false; break; }
            d.setDate(d.getDate() + 1);
        }
        if (ok) setRange({ from, to });
    }, [loadingAvail, form.cabin_id, bookedSet]);

    const cabin = cabins.find((c) => c.id === form.cabin_id);
    const nights = range.from && range.to ? Math.max(0, differenceInCalendarDays(range.to, range.from)) : 0;

    const [rateBreakdown, setRateBreakdown] = useState(null);
    useEffect(() => {
        if (!form.cabin_id || !range.from || !range.to || nights <= 0) {
            setRateBreakdown(null);
            return;
        }
        const ci = format(range.from, "yyyy-MM-dd");
        const co = format(range.to, "yyyy-MM-dd");
        let active = true;
        api.get(`/cabins/${form.cabin_id}/rate-preview`, { params: { check_in: ci, check_out: co } })
            .then((r) => { if (active) setRateBreakdown(r.data); })
            .catch(() => { if (active) setRateBreakdown(null); });
        return () => { active = false; };
    }, [form.cabin_id, range.from, range.to, nights]);

    const lodgingTotal = rateBreakdown?.lodging_subtotal ?? (nights * (cabin?.price_per_night || 0));

    const experiencesTotal = useMemo(() => {
        return Object.entries(form.experiences).reduce((sum, [id, qty]) => {
            const e = experiences.find((x) => x.id === id);
            return sum + (e ? e.price * qty : 0);
        }, 0);
    }, [form.experiences, experiences]);

    const grandTotal = lodgingTotal + experiencesTotal;

    // Loyalty discount preview (lodging only)
    const tierKey = loyalty?.tier?.key;
    const tierName = loyalty?.tier?.name;
    const discountPct = loyalty?.tier?.discount_pct || 0;
    const discountAmount = discountPct > 0 ? lodgingTotal * (discountPct / 100) : 0;
    const finalTotal = Math.max(0, grandTotal - discountAmount);

    const setExperienceQty = (id, qty) => {
        const next = { ...form.experiences };
        if (qty <= 0) delete next[id];
        else next[id] = qty;
        setForm({ ...form, experiences: next });
    };

    // Disable past + booked dates
    const isDisabled = (d) => {
        if (d < new Date(new Date().setHours(0, 0, 0, 0))) return true;
        return bookedSet.has(format(d, "yyyy-MM-dd"));
    };

    // After picking a from-date, also disable any date AT OR AFTER the next booked night
    // to prevent a range from spanning unavailable nights.
    const calendarDisabled = (d) => {
        if (isDisabled(d)) return true;
        if (range.from && !range.to) {
            // when picking the second date, restrict to before the first booked night after range.from
            if (d <= range.from) return false; // allow same/before — the calendar will treat new earlier date as new start
            const it = new Date(range.from);
            it.setDate(it.getDate() + 1);
            while (it <= d) {
                if (bookedSet.has(format(it, "yyyy-MM-dd"))) return true;
                it.setDate(it.getDate() + 1);
            }
        }
        return false;
    };

    const rangeHasBookedDate = useMemo(() => {
        if (!range.from || !range.to) return false;
        const d = new Date(range.from);
        while (d < range.to) {
            if (bookedSet.has(format(d, "yyyy-MM-dd"))) return true;
            d.setDate(d.getDate() + 1);
        }
        return false;
    }, [range, bookedSet]);

    const submit = async () => {
        if (!form.cabin_id || !range.from || !range.to) { toast.error(t("toast.pickDatesError")); return; }
        if (nights <= 0) { toast.error(t("toast.checkoutAfterError")); return; }
        if (rangeHasBookedDate) { toast.error(t("book.warningOverlap")); return; }
        setSubmitting(true);
        try {
            const experiencesPayload = Object.entries(form.experiences).map(([id, qty]) => ({ experience_id: id, quantity: qty }));
            const res = await api.post("/bookings", {
                guest_name: form.guest_name,
                guest_email: form.guest_email,
                guest_phone: form.guest_phone,
                cabin_id: form.cabin_id,
                check_in: format(range.from, "yyyy-MM-dd"),
                check_out: format(range.to, "yyyy-MM-dd"),
                guests: form.guests,
                notes: form.notes,
                experiences: experiencesPayload,
                payment_method: selectedMethod,
            });
            const booking = res.data;

            if (selectedMethod === "stripe") {
                const intent = await api.post("/payments/stripe/intent", { booking_id: booking.id });
                setCheckout({
                    booking,
                    clientSecret: intent.data.client_secret,
                    publishableKey: intent.data.publishable_key,
                });
                setSubmitting(false);
                return;
            }
            if (selectedMethod === "paypal") {
                toast.message(t("book.redirectingPaypal"));
                const order = await api.post("/payments/paypal/order", { booking_id: booking.id, origin: window.location.origin });
                window.location.href = order.data.approval_url;
                return;
            }
            if (selectedMethod === "powertranz") {
                setPtCheckout(booking);
                setSubmitting(false);
                return;
            }
            if (selectedMethod === "bank_transfer") {
                const method = paymentMethods.find((m) => m.key === "bank_transfer");
                setBankInfo({ booking, details: method?.details || {} });
                setSubmitting(false);
                return;
            }
            // reserve-now-pay-later (default)
            toast.success(t("toast.bookingSent"));
            navigate(user ? "/dashboard" : "/");
        } catch (err) {
            toast.error(err.response?.data?.detail || t("toast.bookingFailed"));
            setSubmitting(false);
        }
    };

    const methodLabel = (key) => ({
        stripe: t("book.payCard"),
        paypal: t("book.payPaypal"),
        powertranz: t("book.payPowertranz"),
        bank_transfer: t("book.payBank"),
    }[key] || key);

    return (
        <div data-testid="book-page" className="bg-bone min-h-screen">
            <Navbar />
            <section className="pt-32 lg:pt-40 pb-24 px-6 lg:px-12">
                <div className="max-w-[1300px] mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-7">
                            <div className="eyebrow mb-4">{t("book.eyebrow")}</div>
                            <h1 className="font-chiller text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-10">{t("book.title")}</h1>

                            {/* Cabin select */}
                            <div className="mb-10">
                                <div className="eyebrow mb-4">{t("book.step1")}</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {cabins.map((c) => (
                                        <button
                                            key={c.id}
                                            data-testid={`book-cabin-${c.slug}`}
                                            onClick={() => setForm({ ...form, cabin_id: c.id })}
                                            className={`text-left border ${form.cabin_id === c.id ? "border-forest bg-forest/5" : "border-espresso/15 hover:border-espresso/40"} p-5 transition-all`}
                                        >
                                            <div className="flex justify-between items-start mb-1">
                                                <div className="font-serif text-2xl">{c.name}</div>
                                                {form.cabin_id === c.id && <Check size={18} className="text-forest" />}
                                            </div>
                                            <div className="text-xs uppercase tracking-widest2 text-espresso/70 mb-2">{formatMoney(c.price_per_night)}{t("common.perNight")} · {c.max_guests} {t("common.maxGuests")}</div>
                                            <div className="text-sm text-muted2 line-clamp-2">{c.tagline}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Range date + guests */}
                            <div className="mb-10">
                                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                    <div className="eyebrow">{t("book.step2")}</div>
                                    {form.cabin_id && (
                                        <div className="text-xs text-muted2" data-testid="availability-hint">
                                            {loadingAvail ? t("book.availabilityChecking") : (bookedDates.length === 0 ? t("book.availabilityAll") : t("book.availabilitySome", bookedDates.length))}
                                        </div>
                                    )}
                                </div>
                                <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-widest2 text-espresso/80" data-testid="book-stay-info">
                                    <span className="text-terracotta">{t("cabinsPage.stayInfoTitle")}</span>
                                    <span>{t("cabinsPage.stayCheckIn")}</span>
                                    <span>{t("cabinsPage.stayCheckOut")}</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="eyebrow block mb-2">{t("book.dates")}</label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <button
                                                    data-testid="book-range"
                                                    type="button"
                                                    disabled={!form.cabin_id}
                                                    className="w-full bg-transparent border-b border-espresso/30 hover:border-forest outline-none py-3 text-left flex items-center justify-between disabled:opacity-50"
                                                >
                                                    <span className="flex items-center gap-2 text-sm">
                                                        <CalendarDays size={14} className="text-espresso" />
                                                        {range.from && range.to ? (
                                                            <>{format(range.from, "MMM d")} → {format(range.to, "MMM d, yyyy")}</>
                                                        ) : range.from ? (
                                                            <>{format(range.from, "MMM d, yyyy")} → <span className="text-muted2">{t("book.checkout")}</span></>
                                                        ) : (
                                                            <span className="text-muted2">{t("book.pickDate")}</span>
                                                        )}
                                                    </span>
                                                    <ChevronDown size={14} />
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0 rounded-sm" align="start">
                                                <Calendar
                                                    mode="range"
                                                    selected={range}
                                                    onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
                                                    disabled={calendarDisabled}
                                                    numberOfMonths={2}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div>
                                        <label className="eyebrow block mb-2">{t("book.guests")}</label>
                                        <select
                                            data-testid="book-guests"
                                            value={form.guests}
                                            onChange={(e) => setForm({ ...form, guests: parseInt(e.target.value) })}
                                            className="w-full bg-transparent border-b border-espresso/30 focus:border-forest outline-none py-3"
                                        >
                                            <option value={1}>{t("book.guestN", 1)}</option>
                                            <option value={2}>{t("book.guestN", 2)}</option>
                                        </select>
                                    </div>
                                </div>
                                {rangeHasBookedDate && (
                                    <p className="mt-3 text-sm text-terracotta" data-testid="availability-warning">{t("book.warningOverlap")}</p>
                                )}
                            </div>

                            {/* Experiences */}
                            <BookExperiences
                                experiences={experiences}
                                selected={form.experiences}
                                setExperienceQty={setExperienceQty}
                                formatMoney={formatMoney}
                                t={t}
                            />

                            {/* Guest info */}
                            <div className="mb-10">
                                <div className="eyebrow mb-4">{t("book.step4")}</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Field label={t("book.fullName")} value={form.guest_name} onChange={(v) => setForm({ ...form, guest_name: v })} testid="book-name" />
                                    <Field label={t("book.email")} type="email" value={form.guest_email} onChange={(v) => setForm({ ...form, guest_email: v })} testid="book-email" />
                                    <Field label={t("book.phone")} value={form.guest_phone} onChange={(v) => setForm({ ...form, guest_phone: v })} testid="book-phone" />
                                </div>
                                <div className="mt-6">
                                    <label className="eyebrow block mb-2">{t("book.specialRequests")}</label>
                                    <textarea data-testid="book-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-transparent border-b border-espresso/30 focus:border-forest outline-none py-3" />
                                </div>
                            </div>
                        </div>

                        {/* Summary */}
                        <BookSummary
                            cabin={cabin}
                            range={range}
                            nights={nights}
                            guests={form.guests}
                            lodgingTotal={lodgingTotal}
                            rateBreakdown={rateBreakdown}
                            experiences={experiences}
                            selectedExperiences={form.experiences}
                            discountAmount={discountAmount}
                            tierName={tierName}
                            discountPct={discountPct}
                            finalTotal={finalTotal}
                            paymentMethods={paymentMethods}
                            selectedMethod={selectedMethod}
                            setSelectedMethod={setSelectedMethod}
                            methodLabel={methodLabel}
                            onSubmit={submit}
                            submitting={submitting}
                            rangeHasBookedDate={rangeHasBookedDate}
                            formatMoney={formatMoney}
                            t={t}
                        />
                    </div>
                </div>
            </section>
            <Footer />
            {checkout && (
                <PaymentDialog
                    open={!!checkout}
                    booking={checkout.booking}
                    clientSecret={checkout.clientSecret}
                    publishableKey={checkout.publishableKey}
                    onClose={() => setCheckout(null)}
                    onSuccess={() => {
                        setCheckout(null);
                        toast.success(t("book.paymentSuccess"));
                        navigate(user ? "/dashboard" : "/");
                    }}
                />
            )}
            {bankInfo && (
                <BankTransferDialog
                    open={!!bankInfo}
                    booking={bankInfo.booking}
                    details={bankInfo.details}
                    onClose={() => {
                        setBankInfo(null);
                        toast.success(t("toast.bookingSent"));
                        navigate(user ? "/dashboard" : "/");
                    }}
                />
            )}
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label className="eyebrow block mb-2">{label}</label>
            <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b border-espresso/30 focus:border-forest outline-none py-3" />
        </div>
    );
}
